/**
 * @file       Messages vuex store, used by the whole messages module.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import axios from "@/plugins/axios.js";

/**
 * Gets the default state.
 *
 * @return  {Object}  The default state.
 */
function getDefaultState() {
  return {
    messages: {
      status: null,
      data: { data: [] },
      error: null,
      filters: {
        id: null,
        resourcesPerPage: 5,
        page: 1
      }
    },
    msg: {
      status: null,
      data: null,
      error: null
    }
  };
}

/**
 * Messages vuex actions.
 *
 * @type  {Object}
 */
const actions = {
  /**
   * Get messages.
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getMessages: ({ commit, state }) => {
    const payload = Object.entries(state.messages.filters).reduce(
      (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
      {}
    );
    return new Promise((resolve, reject) => {
      commit("onGetMessages");

      axios
        .get(`teacher/predefinedMessage`, { params: payload })
        .then(response => {
          commit("onGetMessagesSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetMessagesError", error);
          reject(error);
        });
    });
  },

  /**
   * Put message.
   *
   * @param   {Function}  arg1.commit     Commit the action
   * @param   {Object}    payload         The payload
   *
   * @return  {Promise}
   */
  putMsg: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onPutMsg");

      axios
        .put(`teacher/predefinedMessage`, payload)
        .then(response => {
          commit("onPutMsgSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPutMsgError", error);
          reject(error);
        });
    });
  },

  /**
   * Post message.
   *
   * @param   {Function}  arg1.commit     Commit the action
   * @param   {Object}    payload         The payload
   *
   * @return  {Promise}
   */
  postMsg: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onPostMsg");

      axios
        .post(`teacher/predefinedMessage`, payload)
        .then(response => {
          commit("onPostMsgSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPostMsgError", error);
          reject(error);
        });
    });
  },

  /**
   * Delete message.
   *
   * @param   {Function}  arg1.commit     Commit the action
   * @param   {Object}    payload         The payload
   *
   * @return  {Promise}
   */
  deleteMsg: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onDeleteMsg");

      axios
        .delete(`teacher/predefinedMessage`, payload)
        .then(response => {
          commit("onDeleteMsgSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onDeleteMsgError", error);
          reject(error);
        });
    });
  }
};

/**
 * Messages vuex mutations.
 *
 * @type  {Object}
 */
const mutations = {
  /**
   * Get messages.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetMessages: state => {
    state.messages.status = "fetching";
    state.messages.error = {};
  },

  /**
   * Get messages success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetMessagesSuccess: (state, { data }) => {
    state.messages.status = "success";
    state.messages.error = null;
    state.messages.data = data;
  },

  /**
   * Get messages error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetMessagesError: (state, err) => {
    state.messages.status = "error";
    state.messages.error = err;
  },
  /**
   * Put message.
   *
   * @param  {Object}  state   The vuex state
   */
  onPutMsg: state => {
    state.msg.status = "fetching";
    state.msg.error = {};
  },

  /**
   * Put message success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPutMsgSuccess: (state, { data }) => {
    state.msg.status = "success";
    state.msg.error = null;
    state.msg.data = data;
  },

  /**
   * Put message error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPutMsgError: (state, err) => {
    state.msg.status = "error";
    state.msg.error = err;
  },
  /**
   * Post message.
   *
   * @param  {Object}  state   The vuex state
   */
  onPostMsg: state => {
    state.msg.status = "fetching";
    state.msg.error = {};
  },

  /**
   * Post message success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPostMsgSuccess: (state, { data }) => {
    state.msg.status = "success";
    state.msg.error = null;
    state.msg.data = data;
  },

  /**
   * Post message error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPostMsgError: (state, err) => {
    state.msg.status = "error";
    state.msg.error = err;
  },
  /**
   * Delete message.
   *
   * @param  {Object}  state   The vuex state
   */
  onDeleteMsg: state => {
    state.msg.status = "fetching";
    state.msg.error = {};
  },

  /**
   * Delete message success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onDeleteMsgSuccess: (state, { data }) => {
    state.msg.status = "success";
    state.msg.error = null;
    state.msg.data = data;
  },

  /**
   * Delete message error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onDeleteMsgError: (state, err) => {
    state.msg.status = "error";
    state.msg.error = err;
  },

  /**
   * Set messages filters.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  filters     The filters
   */
  setMessagesFilters: (state, filters) => {
    if (filters.reset) {
      state.messages.filters = {
        id: null,
        resourcesPerPage: 5,
        page: 1
      };
    }
    if (filters.fields) {
      state.messages.filters = {
        ...state.messages.filters,
        ...filters.fields
      };
    }
  }
};

/**
 * Export the store.
 *
 * @type  {Object}
 */
export default {
  namespaced: true,
  state: getDefaultState(),
  actions,
  mutations
};
